import type { FieldKind, RecipientRole } from './api'
import { kindNeedsOptions } from './fields'

/** Minimum field shape for page-following logic. */
export type DraftFieldPage = {
  documentIndex: number
  page: number
  placed: boolean
}

/**
 * Unplaced boxes for one recipient track the page the sender is looking at,
 * WITHIN their own document.
 *
 * Never across documents. Following the document too is the obvious reading of
 * "unplaced boxes follow navigation", and it produces two states nobody asked
 * for: selecting a party while looking at the schedule silently empties the
 * contract, and doing it twice puts two of their signature boxes on one
 * document, which the server refuses. Moving a box between documents is always
 * explicit — "Move it here" on a placed box, or the control that adds a
 * signature to the document being looked at.
 */
export function followPageForRecipient<T extends { fields: DraftFieldPage[] }>(
  rs: T[],
  ri: number,
  pageNum: number,
  documentIndex = 0,
): T[] {
  return rs.map((r, i) => (
    i === ri
      ? {
          ...r,
          fields: r.fields.map((f) => (
            !f.placed && f.documentIndex === documentIndex ? { ...f, page: pageNum } : f
          )),
        }
      : r
  ))
}

/**
 * Which documents a party has boxes on, in order.
 *
 * The server refuses a party with boxes on a document and no signature box on
 * it, because what a signer types is stamped in and then sealed: a document
 * they filled in but did not sign would carry their answers outside any
 * signature of theirs. The sender should hear that here rather than on submit.
 */
export function documentsOf<T extends { documentIndex: number }>(fields: T[]): number[] {
  const seen = new Set(fields.map((f) => f.documentIndex))
  return [...seen].sort((a, b) => a - b)
}

/**
 * What is wrong with a signing party's boxes, or null when nothing is.
 *
 * Two things the server refuses, said in one place so the sender is told before
 * they press send rather than after:
 *
 * - no boxes at all, which is a party who was never asked for anything. It is
 *   reachable by removing the document their only boxes were on, and it used to
 *   be a dead end: the send was refused with "place a signature box for them",
 *   and with one document left there was no control on the page that could
 *   place one.
 * - boxes on a document with no signature box on it.
 * - two signature boxes on one document.
 */
export function whatIsMissing<T extends { documentIndex: number; kind: string }>(
  fields: T[],
): Missing | null {
  if (fields.length === 0) return { kind: 'nothing-to-sign' }
  for (const at of documentsOf(fields)) {
    const signatures = fields.filter((f) => f.documentIndex === at && f.kind === 'signature').length
    if (signatures === 0) return { kind: 'unsigned-document', documentIndex: at }
    // The seal is anchored to a signature box, so two of them on one document
    // are two places the mark could go and no way to say which. The server
    // refuses it; this is the same rule said where the sender can act on it.
    if (signatures > 1) return { kind: 'two-signatures', documentIndex: at }
  }
  return null
}

/** What is wrong with a signing party's boxes. */
export type Missing =
  | { kind: 'nothing-to-sign' }
  | { kind: 'unsigned-document'; documentIndex: number }
  | { kind: 'two-signatures'; documentIndex: number }

export function signsDocument(role: RecipientRole): boolean {
  return role === 'signer' || role === 'witness'
}

/**
 * The party the placing step opens on: the first who signs, or the first of
 * all when nobody does. Send again opened on the first row, and a viewer
 * first was then told it "has no boxes. Add a signature for them" (L18).
 */
export function firstSigningParty(roles: RecipientRole[]): number {
  return Math.max(0, roles.findIndex(signsDocument))
}

/** A visibility rule in the draft, keyed by the controller's stable field key. */
export type ShowWhenDraft = { fieldKey: string; op: 'checked' | 'eq'; value?: string }

/** Whether this kind of box can hide another. */
export function canControlShowWhen(kind: string): boolean {
  return kind === 'checkbox' || kind === 'radio' || kind === 'dropdown' || kind === 'text'
}

/**
 * Every box of a signing party set to "Show only when" a box of a party who
 * does not sign. A party who views or receives a copy is sent with no boxes,
 * so the server cannot find the box the rule names and refuses the whole send
 * with 400 bad_show_when, which names no box. It happens when the
 * controller's party is changed to such a role after the rule was set: the
 * page keeps their boxes in the draft, so changing the role back restores
 * them. The same shape as the app's model/rules.ts, so the app can run this
 * one in place (review-2-app R3).
 */
export function showWhenOnNonSigners<T extends {
  key: string
  role: RecipientRole
  fields: { key: string; documentIndex: number; showWhen?: ShowWhenDraft }[]
}>(recipients: T[]): { recipientKey: string; fieldKey: string; documentIndex: number; controllerIndex: number }[] {
  return recipients.flatMap((r) => {
    if (!signsDocument(r.role)) return []
    return r.fields.flatMap((f) => {
      if (!f.showWhen) return []
      const at = locateFieldKey(recipients, f.showWhen.fieldKey)
      if (!at || signsDocument(recipients[at.ri].role)) return []
      return [{ recipientKey: r.key, fieldKey: f.key, documentIndex: f.documentIndex, controllerIndex: at.ri }]
    })
  })
}

/**
 * What makes a "Show only when" rule one the server refuses, or one that can
 * never show its box (review-3):
 * - `no-answer`: an "equal to" rule with no answer, as the picker leaves it on
 *   a Text box until the sender types one. The server refuses it
 *   (visibility.go NormalizeShowWhen), 400 bad_show_when.
 * - `not-an-option`: the rule waits for a choice the radio or dropdown no
 *   longer offers, because the option was renamed or removed after the rule
 *   was set. The server accepts it, but no answer equals it, so the box is
 *   never shown and, hidden, is not required: the document finishes without it.
 * - `other-person`: the box waits on a box of another signing party. A
 *   signer is shown only their own boxes when signing, so the rule could
 *   never be met, and the server refuses it (applyFieldVisibilityTx,
 *   ErrShowWhenOtherPerson), 400 bad_show_when (review 4, docs-1). Read
 *   before the loop, so a loop through two people is named at each box.
 * - `loop`: boxes that wait on each other, A shown only when B and B only when
 *   A. The server refuses it (DetectShowWhenCycles), 400 bad_show_when. Said
 *   once per loop, at its first box in the roster's order.
 * Only a signing party's boxes are sent, so only theirs are read; a rule on a
 * non-signer's box is showWhenOnNonSigners's to say. Moved here unchanged from
 * the app's compose/model/rules.ts, so the app and the web run one rule
 * (review-3-app request 1).
 */
export type ShowWhenFault = 'no-answer' | 'not-an-option' | 'other-person' | 'loop'

export function showWhenUnanswerable<T extends {
  key: string
  role: RecipientRole
  fields: { key: string; kind: FieldKind; documentIndex: number; options?: string[]; showWhen?: { fieldKey: string; op: 'checked' | 'eq'; value?: string } }[]
}>(recipients: T[]): { recipientKey: string; fieldKey: string; documentIndex: number; controllerIndex: number; fault: ShowWhenFault }[] {
  const at = (key: string) => {
    const l = locateFieldKey(recipients, key)
    return l ? { ...l, r: recipients[l.ri], f: recipients[l.ri].fields[l.fi] } : null
  }
  /** The boxes on the loop through `start`, in the order they wait on each other, or null when there is none. */
  const loopThrough = (startKey: string): string[] | null => {
    const seen: string[] = [startKey]
    let key = at(startKey)?.f.showWhen?.fieldKey
    while (key !== undefined) {
      if (key === startKey) return seen
      if (seen.includes(key)) return null
      const next = at(key)
      if (!next || !signsDocument(next.r.role)) return null
      seen.push(key)
      key = next.f.showWhen?.fieldKey
    }
    return null
  }
  const order = recipients.flatMap((r) => r.fields.map((f) => f.key))
  return recipients.flatMap((r, ri) => {
    if (!signsDocument(r.role)) return []
    return r.fields.flatMap((f) => {
      const rule = f.showWhen
      if (!rule) return []
      const ctrl = at(rule.fieldKey)
      if (!ctrl || !signsDocument(ctrl.r.role)) return []
      const found = (fault: ShowWhenFault) => [{ recipientKey: r.key, fieldKey: f.key, documentIndex: f.documentIndex, controllerIndex: ctrl.ri, fault }]
      if (ctrl.ri !== ri) return found('other-person')
      const loop = loopThrough(f.key)
      if (loop) {
        const first = loop.reduce((a, b) => (order.indexOf(b) < order.indexOf(a) ? b : a))
        return first === f.key ? found('loop') : []
      }
      if (rule.op !== 'eq') return []
      const value = (rule.value ?? '').trim()
      if (value === '') return found('no-answer')
      if (kindNeedsOptions(ctrl.f.kind)) {
        const choices = (ctrl.f.options ?? []).map((o) => o.trim()).filter(Boolean)
        if (!choices.includes(value)) return found('not-an-option')
      }
      return []
    })
  })
}

/** Why the server would refuse a radio's or dropdown's options. */
export type OptionsFault = 'too-few' | 'repeated'
export interface OptionsRefusal { recipientKey: string; fieldKey: string; documentIndex: number; fault: OptionsFault; repeated?: string }

/**
 * Every radio and dropdown of a signing party whose choices the server
 * refuses with 400 bad_field_options (internal/store/envelope.go
 * ValidateFieldOptions): fewer than two, or one repeated once trimmed. The
 * options are read as they are sent (each trimmed, the empty ones dropped),
 * so a cleared option counts only when it leaves fewer than two. The page
 * left Send on and the server's refusal named no box. Moved here unchanged
 * from the app's compose/model/rules.ts, so the app and the web run one rule
 * (review 4, app request 3).
 */
export function optionsRefused<T extends {
  key: string
  role: RecipientRole
  fields: readonly { key: string; kind: FieldKind; documentIndex: number; options?: readonly string[] }[]
}>(recipients: readonly T[]): OptionsRefusal[] {
  return recipients.flatMap((r): OptionsRefusal[] => {
    if (!signsDocument(r.role)) return []
    return r.fields.flatMap((f): OptionsRefusal[] => {
      if (!kindNeedsOptions(f.kind)) return []
      const sent = (f.options ?? []).map((o) => o.trim()).filter(Boolean)
      const at = { recipientKey: r.key, fieldKey: f.key, documentIndex: f.documentIndex }
      if (sent.length < 2) return [{ ...at, fault: 'too-few' }]
      const repeated = sent.find((o, i) => sent.indexOf(o) !== i)
      return repeated !== undefined ? [{ ...at, fault: 'repeated', repeated }] : []
    })
  })
}

/** Find a field by its draft key across every recipient. */
export function locateFieldKey<T extends { fields: { key: string }[] }>(
  recipients: T[],
  key: string,
): { ri: number; fi: number } | null {
  for (let ri = 0; ri < recipients.length; ri++) {
    const fi = recipients[ri].fields.findIndex((f) => f.key === key)
    if (fi >= 0) return { ri, fi }
  }
  return null
}

/**
 * Drop visibility rules that pointed at a box that is no longer there.
 * A rule that names a deleted box would be refused at send.
 */
export function dropDanglingShowWhen<T extends { fields: { key: string; showWhen?: ShowWhenDraft }[] }>(
  recipients: T[],
): T[] {
  const keys = new Set(recipients.flatMap((r) => r.fields.map((f) => f.key)))
  return recipients.map((r) => ({
    ...r,
    fields: r.fields.map((f) => (
      f.showWhen && !keys.has(f.showWhen.fieldKey) ? { ...f, showWhen: undefined } : f
    )),
  }))
}

/**
 * How many signing links a send creates: one for each person who signs,
 * witnesses or views, and one for each member of a signing group, since any
 * one of them may sign. A copy recipient is sent a copy, not a link. The
 * button counted rows, so a group alone was "Create signing link" although
 * each member gets one.
 */
export function signingLinkCount(rows: { role: RecipientRole; members?: unknown[] }[]): number {
  return rows.reduce((n, r) => (r.role === 'cc' ? n : n + (r.members?.length || 1)), 0)
}

